/**
 * 一覧画面の表示状態（ページ番号・フィルタ・ソート）を URL クエリとして扱うための純粋関数群。
 *
 * React に依存しないため単体テストが容易で、`useUrlState` から利用される。
 */

/** URL クエリで扱える値の型。 */
export type UrlStateValue = string | number | boolean;

/** URL クエリとして管理する状態のスキーマ。キーがクエリ名になる。 */
export type UrlStateShape = Record<string, UrlStateValue>;

/** キーごとに許可する値の一覧。URL に不正値が入った場合の防御に使う。 */
export type UrlStateAllowed<S extends UrlStateShape> = {
  [K in keyof S]?: readonly S[K][];
};

/**
 * クエリ文字列を既定値の型に合わせて復元する。
 * 型が合わない値や `allowed` に無い値は既定値へフォールバックする。
 */
export function readUrlState<S extends UrlStateShape>(
  params: URLSearchParams,
  defaults: S,
  allowed?: UrlStateAllowed<S>
): S {
  const entries = Object.entries(defaults).map(([key, fallback]) => {
    const raw = params.get(key);
    const parsed = parseValue(raw, fallback);
    const candidates = allowed?.[key];

    if (candidates && !(candidates as readonly UrlStateValue[]).includes(parsed)) {
      return [key, fallback];
    }

    return [key, parsed];
  });

  return Object.fromEntries(entries) as S;
}

/**
 * 現在のクエリへ差分を適用した新しい `URLSearchParams` を返す。
 *
 * - 既定値と同じ値はクエリから削除し、URL を短く保つ
 * - スキーマ外のクエリ（他画面から渡された `userId` など）はそのまま保持する
 * - 引数の `params` は変更しない
 */
export function writeUrlState<S extends UrlStateShape>(
  params: URLSearchParams,
  defaults: S,
  patch: Partial<S>
): URLSearchParams {
  const next = new URLSearchParams(params);

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      continue;
    }

    if (value === defaults[key]) {
      next.delete(key);
      continue;
    }

    next.set(key, String(value));
  }

  return next;
}

/** 既定値の型に合わせてクエリ値を変換する。変換できない場合は既定値を返す。 */
function parseValue<T extends UrlStateValue>(raw: string | null, fallback: T): T {
  if (raw === null) {
    return fallback;
  }

  if (typeof fallback === 'number') {
    const parsed = Number(raw);
    return (raw.trim() !== '' && Number.isFinite(parsed) ? parsed : fallback) as T;
  }

  if (typeof fallback === 'boolean') {
    if (raw === 'true') {
      return true as T;
    }
    if (raw === 'false') {
      return false as T;
    }
    return fallback;
  }

  return raw as T;
}
